import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Eye, Edit2, MoreVertical, CheckCircle, Play, Send, Calendar, XCircle } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardHeader, CardBody, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    Modal, ConfirmDialog, toast
} from '../../components/common';
import { budgetApi } from '../../services/api';
import BudgetForm from './BudgetForm';
import './Budgets.css';

export default function BudgetList() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showForm, setShowForm] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);
    const [viewingBudget, setViewingBudget] = useState(null);
    const [actionBudget, setActionBudget] = useState(null);
    const [actionType, setActionType] = useState('');

    // Fetch active fiscal year
    const { data: fiscalYear } = useQuery({
        queryKey: ['activeFiscalYear'],
        queryFn: () => budgetApi.getActiveFiscalYear(),
        select: (res) => res.data?.data
    });

    // Fetch budgets
    const { data, isLoading } = useQuery({
        queryKey: ['budgets', page, pageSize, search, statusFilter],
        queryFn: () => budgetApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    // Action mutations
    const actionMutation = useMutation({
        mutationFn: ({ id, action, data }) => {
            switch (action) {
                case 'submit': return budgetApi.submit(id);
                case 'approve': return budgetApi.approve(id, data);
                case 'activate': return budgetApi.activate(id);
                case 'close': return budgetApi.close(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const messages = {
                submit: 'Budget submitted for approval',
                approve: 'Budget approved successfully',
                activate: 'Budget activated successfully',
                close: 'Budget closed successfully'
            };
            toast.success(messages[action] || `Budget ${action} completed`);
            queryClient.invalidateQueries(['budgets']);
            setActionBudget(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const variants = {
            'draft': 'default',
            'submitted': 'warning',
            'approved': 'info',
            'active': 'success',
            'closed': 'default'
        };
        return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
    };

    const calculateUtilization = (used, total) => {
        const usedAmount = parseFloat(used) || 0;
        const totalAmount = parseFloat(total) || 0;
        if (totalAmount === 0) return 0;
        return Math.round((usedAmount / totalAmount) * 100);
    };

    const columns = [
        {
            key: 'code',
            title: 'Budget Code',
            dataIndex: 'budgetCode',
            width: 140,
            render: (code) => <span className="budget-code">{code}</span>
        },
        {
            key: 'department',
            title: 'Department',
            dataIndex: 'department',
            render: (dept) => dept?.name || '-'
        },
        {
            key: 'fiscalYear',
            title: 'Fiscal Year',
            dataIndex: 'fiscalYear',
            width: 100,
            render: (fy) => fy?.yearCode || '-'
        },
        {
            key: 'amount',
            title: 'Total Amount',
            dataIndex: 'totalAmount',
            align: 'right',
            render: (amount) => formatCurrency(amount)
        },
        {
            key: 'utilization',
            title: 'Utilization',
            width: 150,
            render: (_, row) => {
                const usedAmount = (parseFloat(row.committedAmount) || 0) + (parseFloat(row.spentAmount) || 0);
                const util = calculateUtilization(usedAmount, row.totalAmount);
                return (
                    <div className="utilization-cell">
                        <div className="utilization-bar">
                            <div
                                className={`utilization-fill ${util > 90 ? 'danger' : util > 70 ? 'warning' : ''}`}
                                style={{ width: `${Math.min(util, 100)}%` }}
                            />
                        </div>
                        <span className="utilization-text">{util}%</span>
                    </div>
                );
            }
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            width: 100,
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); setViewingBudget(row); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'draft' && (
                                <>
                                    <DropdownItem icon={Edit2} onClick={() => { close(); setEditingBudget(row); setShowForm(true); }}>
                                        Edit
                                    </DropdownItem>
                                    <DropdownItem icon={Send} onClick={() => { close(); setActionBudget(row); setActionType('submit'); }}>
                                        Submit
                                    </DropdownItem>
                                </>
                            )}
                            {row.status === 'submitted' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionBudget(row); setActionType('approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                            {row.status === 'approved' && (
                                <DropdownItem icon={Play} onClick={() => { close(); setActionBudget(row); setActionType('activate'); }}>
                                    Activate
                                </DropdownItem>
                            )}
                            {row.status === 'active' && (
                                <DropdownItem icon={XCircle} onClick={() => { close(); setActionBudget(row); setActionType('close'); }}>
                                    Close Budget
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'submitted', label: 'Submitted' },
        { value: 'approved', label: 'Approved' },
        { value: 'active', label: 'Active' },
        { value: 'closed', label: 'Closed' }
    ];

    return (
        <div className="budgets-page">
            <PageHeader
                title="Budget Management"
                subtitle={fiscalYear ? `Fiscal Year: ${fiscalYear.yearCode}` : 'Manage department budgets'}
                breadcrumbs={[{ label: 'Budgets', path: '/budgets' }]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Create Budget
                    </Button>
                }
            />

            <Card>
                <div className="budgets-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search budgets..."
                        className="budgets-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="budgets-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No budgets found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="budgets-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            {/* Budget Form Modal */}
            <Modal
                isOpen={showForm}
                onClose={() => { setShowForm(false); setEditingBudget(null); }}
                title={editingBudget ? 'Edit Budget' : 'Create Budget'}
                size="lg"
            >
                <BudgetForm
                    budget={editingBudget}
                    fiscalYearId={fiscalYear?.id}
                    onSuccess={() => {
                        setShowForm(false);
                        setEditingBudget(null);
                        queryClient.invalidateQueries(['budgets']);
                    }}
                    onCancel={() => { setShowForm(false); setEditingBudget(null); }}
                />
            </Modal>

            {/* Action Confirmation */}
            <ConfirmDialog
                isOpen={!!actionBudget}
                onClose={() => { setActionBudget(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionBudget?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Budget`}
                message={`Are you sure you want to ${actionType} this budget?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />

            {/* Budget Detail Modal */}
            <Modal
                isOpen={!!viewingBudget}
                onClose={() => setViewingBudget(null)}
                title={`Budget Details: ${viewingBudget?.budgetCode || ''}`}
                size="lg"
            >
                {viewingBudget && (
                    <div style={{ padding: '0.5rem 0' }}>
                        {/* Budget Info */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                Budget Information
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>BUDGET CODE</label>
                                    <span style={{ fontSize: '0.9375rem', color: '#1e293b', fontWeight: 600 }}>{viewingBudget.budgetCode}</span>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>STATUS</label>
                                    <span>{getStatusBadge(viewingBudget.status)}</span>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>DEPARTMENT</label>
                                    <span style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{viewingBudget.department?.name || '-'}</span>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block' }}>FISCAL YEAR</label>
                                    <span style={{ fontSize: '0.9375rem', color: '#1e293b' }}>{viewingBudget.fiscalYear?.yearCode || '-'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Budget Amounts */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                Budget Amounts
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                <div style={{
                                    padding: '1rem',
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    borderRadius: '0.75rem',
                                    border: '1px solid #bfdbfe'
                                }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#3b82f6', display: 'block' }}>TOTAL AMOUNT</label>
                                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(viewingBudget.totalAmount)}</span>
                                </div>
                                <div style={{
                                    padding: '1rem',
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    borderRadius: '0.75rem',
                                    border: '1px solid #fcd34d'
                                }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#d97706', display: 'block' }}>USED AMOUNT</label>
                                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#92400e' }}>{formatCurrency((parseFloat(viewingBudget.committedAmount) || 0) + (parseFloat(viewingBudget.spentAmount) || 0))}</span>
                                </div>
                                <div style={{
                                    padding: '1rem',
                                    background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                                    borderRadius: '0.75rem',
                                    border: '1px solid #86efac'
                                }}>
                                    <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#16a34a', display: 'block' }}>REMAINING</label>
                                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#166534' }}>{formatCurrency((parseFloat(viewingBudget.totalAmount) || 0) - (parseFloat(viewingBudget.committedAmount) || 0) - (parseFloat(viewingBudget.spentAmount) || 0))}</span>
                                </div>
                            </div>
                        </div>

                        {/* Utilization */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{
                                fontSize: '0.9375rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                marginBottom: '1rem',
                                paddingBottom: '0.5rem',
                                borderBottom: '2px solid #e2e8f0'
                            }}>
                                Utilization
                            </h4>
                            <div style={{
                                height: '24px',
                                background: '#e2e8f0',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                marginBottom: '0.5rem'
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.min(calculateUtilization((parseFloat(viewingBudget.committedAmount) || 0) + (parseFloat(viewingBudget.spentAmount) || 0), viewingBudget.totalAmount), 100)}%`,
                                    background: calculateUtilization((parseFloat(viewingBudget.committedAmount) || 0) + (parseFloat(viewingBudget.spentAmount) || 0), viewingBudget.totalAmount) > 90
                                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                                        : calculateUtilization((parseFloat(viewingBudget.committedAmount) || 0) + (parseFloat(viewingBudget.spentAmount) || 0), viewingBudget.totalAmount) > 70
                                            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                            : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                    borderRadius: '12px',
                                    transition: 'width 0.5s ease'
                                }} />
                            </div>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b', textAlign: 'center' }}>
                                <strong style={{ fontSize: '1rem', color: '#1e293b' }}>
                                    {calculateUtilization((parseFloat(viewingBudget.committedAmount) || 0) + (parseFloat(viewingBudget.spentAmount) || 0), viewingBudget.totalAmount)}%
                                </strong> of budget utilized
                            </p>
                        </div>

                        {/* Notes */}
                        {viewingBudget.notes && (
                            <div style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{
                                    fontSize: '0.9375rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    marginBottom: '0.5rem'
                                }}>
                                    Notes
                                </h4>
                                <p style={{
                                    margin: 0,
                                    padding: '1rem',
                                    background: '#f8fafc',
                                    borderRadius: '0.5rem',
                                    color: '#64748b',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6
                                }}>
                                    {viewingBudget.notes}
                                </p>
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.75rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #e2e8f0'
                        }}>
                            <Button
                                variant="secondary"
                                onClick={() => setViewingBudget(null)}
                            >
                                Close
                            </Button>
                            {viewingBudget.status === 'draft' && (
                                <Button
                                    icon={Edit2}
                                    onClick={() => {
                                        setEditingBudget(viewingBudget);
                                        setViewingBudget(null);
                                        setShowForm(true);
                                    }}
                                >
                                    Edit Budget
                                </Button>
                            )}
                            {viewingBudget.status === 'pending' && (
                                <Button
                                    icon={CheckCircle}
                                    onClick={() => {
                                        setActionBudget(viewingBudget);
                                        setActionType('approve');
                                        setViewingBudget(null);
                                    }}
                                >
                                    Approve
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
